import { Component, type ErrorInfo, type ReactNode } from 'react';
import { track } from '../lib/analytics';
import { CONTACT } from '../config/contact';

interface Props {
  children: ReactNode;
  sectionName?: string;
}

interface State {
  hasError: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
    if (this.props.sectionName) {
      track('error_boundary_shown', {
        section_name: this.props.sectionName,
        error_type: 'boundary',
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            background: '#FAF9F7',
            border: '1px solid #E7E3DC',
            borderRadius: 12,
            padding: 26,
            maxWidth: 560,
            margin: '24px auto',
          }}
        >
          <p
            style={{
              margin: '0 0 12px',
              fontSize: 16,
              fontWeight: 600,
              color: '#201F1D',
            }}
          >
            Something on this page broke. The rest of the site still works.
          </p>
          <p
            style={{
              margin: '0 0 16px',
              fontSize: 14,
              lineHeight: 1.6,
              color: '#55524C',
            }}
          >
            Call us on{' '}
            <a
              href={CONTACT.phoneHref}
              style={{ color: '#B68235', fontWeight: 600 }}
              onClick={() =>
                track('phone_click', { element_location: 'error_boundary' })
              }
            >
              {CONTACT.phoneDisplay}
            </a>{' '}
            or write to{' '}
            <a
              href={`mailto:${CONTACT.email}`}
              style={{ color: '#B68235', fontWeight: 600 }}
              onClick={() =>
                track('email_click', { element_location: 'error_boundary' })
              }
            >
              {CONTACT.email}
            </a>
            .
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              border: 'none',
              background: '#B68235',
              color: '#201F1D',
              fontFamily: 'inherit',
              fontWeight: 600,
              fontSize: 15,
              padding: '10px 18px',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
